/**
 * Pure, dependency-free domain logic shared by the API and the React app: time-zone / callable
 * computation, the lead-qualification readiness model, disposition lookups, and status
 * normalization. Kept side-effect-free so it can be unit-tested without a DB or a browser.
 */
import { STATE_TZ, TZ_BUCKETS, CALLABLE_START_HOUR, CALLABLE_END_HOUR, DISPOSITIONS, STATUSES, CORE_STATUSES, LEGACY_STATUS_MAP, DISQUALIFIED_STATUSES, } from './constants.js';
// ---- Time zones / callable window -----------------------------------------------------------
const TZ_IANA = Object.fromEntries(TZ_BUCKETS.map((b) => [b.key, b.tz]));
// Formatter construction loads ICU data and is expensive in bulk scans. There are only six
// supported zones; reuse formatters while formatting each supplied instant (including DST).
const hourFormatters = new Map();
const labelFormatters = new Map();
function timeFormatter(tz, label) {
    const cache = label ? labelFormatters : hourFormatters;
    let formatter = cache.get(tz);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: TZ_IANA[tz],
            hour: 'numeric',
            minute: label ? '2-digit' : 'numeric',
            hour12: label,
        });
        cache.set(tz, formatter);
    }
    return formatter;
}
/** State (2-letter, any case) → time-zone bucket. Unknown/blank states default to ET. */
export function tzForState(state) {
    const key = (state ?? '').trim().toUpperCase();
    return STATE_TZ[key] ?? 'ET';
}
/** The local hour (0–23, may be fractional) in a given time-zone bucket at instant `now`. */
export function localHour(tz, now) {
    const parts = timeFormatter(tz, false).formatToParts(now);
    const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
    const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    return h + m / 60;
}
/** A short local-time label like "2:39 PM CT" for a lead in the given state. */
export function localTimeLabel(state, now) {
    const tz = tzForState(state);
    const t = timeFormatter(tz, true).format(now);
    return `${t} ${tz}`;
}
/** True when the lead's local time is inside the callable business-hours window (8AM–6PM). */
export function isCallable(state, now) {
    const h = localHour(tzForState(state), now);
    return h >= CALLABLE_START_HOUR && h < CALLABLE_END_HOUR;
}
/** Stored statuses (current + legacy) that should never appear in the callable queue. Won/Lost are
 *  closed outcomes, so reps stop seeing them just like Converted/Disqualified. */
export const NONCALLABLE_STATUSES = [
    'Disqualified',
    'Converted',
    'Won',
    'Lost',
    'SQL',
    'Not Interested',
    'Bad Data',
    'Do Not Call',
];
/** Time-zone buckets whose local time is currently inside the callable window. */
export function callableTzKeys(now) {
    return TZ_BUCKETS.filter((b) => {
        const h = localHour(b.key, now);
        return h >= CALLABLE_START_HOUR && h < CALLABLE_END_HOUR;
    }).map((b) => b.key);
}
/** 2-letter states whose bucket is currently callable (for SQL `state = ANY(...)` filters). */
export function callableStateList(now) {
    const keys = callableTzKeys(now);
    return Object.entries(STATE_TZ)
        .filter(([, tz]) => keys.includes(tz))
        .map(([st]) => st);
}
/** States belonging to a given time-zone bucket (ET/CT/MT/PT/AK/HI). */
export function statesForTz(tzKey) {
    return Object.entries(STATE_TZ)
        .filter(([, tz]) => tz === tzKey)
        .map(([st]) => st);
}
// ---- Dispositions -----------------------------------------------------------------------------
export function dispositionByCode(code) {
    return DISPOSITIONS.find((d) => d.code === code);
}
/**
 * How each one-click call disposition contributes to the rep's daily activity tally — the "running
 * total" the legacy activity screen kept automatically. Every *phone* disposition adds a dial;
 * outcomes where a person was actually reached add a connect; codes with a direct Activity-field
 * counterpart increment it. Non-phone touches (Email / LinkedIn / Webinar) add their own field but
 * no dial. On-Site Visit is an in-person touch (a connect, not a phone dial). The "✉ also emailed"
 * flag on any touch adds one email on top (handled by the caller, not here).
 */
const OUTCOME_TALLY = {
    NA: { dials: 1, noanswer: 1 },
    LMWP: { dials: 1, lmwp: 1, connects: 1 },
    VM: { dials: 1, vm: 1 },
    ES: { emails: 1 },
    CB: { dials: 1, callback: 1, connects: 1 },
    NI: { dials: 1, notint: 1, connects: 1 },
    WI: { webinv: 1 },
    WR: { webreg: 1 },
    OSV: { connects: 1 },
    DQ: { dials: 1, connects: 1 },
    BPN: { dials: 1, badphone: 1 },
    DNC: { dials: 1, remove: 1 },
    MS: { dials: 1, qls: 1, connects: 1 },
};
/** The activity increments for a single logged call outcome (empty for unknown/blank codes). */
export function activityForOutcome(code) {
    return OUTCOME_TALLY[(code ?? '').trim()] ?? {};
}
/**
 * The single definition of "connects" (conversations where a person was reached) computed from a
 * stored Activity row: the higher of the saved/derived `connects` value and the reached-a-person
 * outcome columns that exist (LMWP + Callback + Not-Interested). Every screen uses this so the
 * dashboard, monthly rollup, and CSV export agree instead of each computing connects differently.
 * (On-Site / Meeting-Set / DQ also count as connects but have no dedicated column, so they are only
 * reflected via the stored `connects` value — hence the GREATEST/higher-of.)
 */
export function reachedConnects(r) {
    return Math.max(r.connects ?? 0, (r.lmwp ?? 0) + (r.callback ?? 0) + (r.notInt ?? 0));
}
/** SQL fragment matching reachedConnects(), for aggregate queries over the "Activity" table. */
export const REACHED_CONNECTS_SQL = 'GREATEST(connects, lmwp + callback + "notInt")';
// ---- Contact normalization / validation -------------------------------------------------------
/** Trim + lowercase an email for consistent storage and dedup. */
export function normalizeEmail(email) {
    return (email ?? '').trim().toLowerCase();
}
/** True when blank (email is optional) or a plausibly-valid email address. */
export function isValidEmail(email) {
    const s = (email ?? '').trim();
    return s === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}
/**
 * Normalize a phone for consistent storage: US 10-digit → "(123) 456-7890", US 11-digit (leading 1)
 * → "+1 (123) 456-7890". Anything else (international, extensions, partial) is left trimmed as-is so
 * we never corrupt a number we don't understand.
 */
export function normalizePhone(phone) {
    const raw = (phone ?? '').trim();
    if (!raw)
        return '';
    const digits = raw.replace(/\D/g, '');
    if (!raw.startsWith('+') && digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (!raw.startsWith('+') && digits.length === 11 && digits[0] === '1') {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return raw;
}
/**
 * Quick follow-up presets ("+1 day / +3 days / +1 week / next Monday"), each at 9:00am local on the
 * target day, computed from `now`. Pure so it can be unit-tested; the caller stores `iso` on
 * Lead.followUp via the normal update path.
 */
export function snoozePresets(now) {
    const at9am = (base, addDays) => {
        const d = new Date(base);
        d.setDate(d.getDate() + addDays);
        d.setHours(9, 0, 0, 0);
        return d.toISOString();
    };
    // Days until next Monday (always in the future: 1–7).
    const daysToMonday = ((8 - now.getDay()) % 7) || 7;
    return [
        { key: '1d', label: 'Tomorrow', iso: at9am(now, 1) },
        { key: '3d', label: '+3 days', iso: at9am(now, 3) },
        { key: '1w', label: '+1 week', iso: at9am(now, 7) },
        { key: 'mon', label: 'Next Mon', iso: at9am(now, daysToMonday) },
    ];
}
/** Add `src` onto `acc` in place (scaled by `times`, default 1) and return `acc`. */
export function addActivityCounts(acc, src, times = 1) {
    for (const [k, v] of Object.entries(src)) {
        const key = k;
        acc[key] = (acc[key] ?? 0) + (v ?? 0) * times;
    }
    return acc;
}
/** Sum a lead's call history into activity counts for a single YYYY-MM-DD date. */
export function tallyCallsForDate(calls, date) {
    const acc = {};
    const d = date.slice(0, 10);
    for (const c of calls ?? []) {
        if (!c || (c.date ?? '').slice(0, 10) !== d)
            continue;
        addActivityCounts(acc, activityForOutcome(c.outcome));
        if (c.emailedToo)
            acc.emails = (acc.emails ?? 0) + 1;
    }
    return acc;
}
// ---- Status normalization ---------------------------------------------------------------------
const STATUS_SET = new Set(STATUSES);
const DISQUALIFIED_SET = new Set(DISQUALIFIED_STATUSES);
/** Fold a legacy/free-text status onto the current vocabulary (defaults to 'New' when unknown). */
export function normalizeStatus(status) {
    const s = (status ?? '').trim();
    if (STATUS_SET.has(s))
        return s;
    if (LEGACY_STATUS_MAP[s])
        return LEGACY_STATUS_MAP[s];
    return 'New';
}
/**
 * Fold only known legacy aliases onto the current vocabulary while preserving any other value
 * as-is (unlike normalizeStatus, which collapses unknowns to 'New'). Used for status counters so
 * admin-added *custom* statuses keep their own bucket instead of being lumped under 'New'.
 */
export function foldLegacyStatus(status) {
    const s = (status ?? '').trim();
    if (!s)
        return 'New';
    if (STATUS_SET.has(s))
        return s;
    return LEGACY_STATUS_MAP[s] ?? s;
}
/** True when a stored status means "disqualified/closed-lost" — drives the retention clock + sweep
 *  (Lost is treated the same as Disqualified). Matches both raw legacy values and the canonical fold. */
export function isDisqualifiedStatus(status) {
    const s = (status ?? '').trim();
    return DISQUALIFIED_SET.has(s) || DISQUALIFIED_SET.has(normalizeStatus(s));
}
/** True when `name` is a built-in status with fixed semantics (cannot be renamed/deleted in admin). */
export function isCoreStatus(name) {
    return CORE_STATUSES.includes((name ?? '').trim());
}
/**
 * Merge a saved status config over the built-in defaults into a clean ordered list: de-dupes,
 * drops blanks, preserves the admin's order + hidden flags, and appends any built-in status the
 * saved config is missing (so a core stage can never be lost). Pure — used by both API and web.
 */
export function normalizeStatusConfig(raw) {
    const arr = Array.isArray(raw) ? raw : [];
    const seen = new Set();
    const out = [];
    for (const e of arr) {
        const name = (typeof e === 'string' ? e : String(e?.name ?? '')).trim();
        if (!name || seen.has(name))
            continue;
        seen.add(name);
        out.push({ name, hidden: !!e?.hidden });
    }
    for (const s of CORE_STATUSES) {
        if (!seen.has(s)) {
            seen.add(s);
            out.push({ name: s });
        }
    }
    return out;
}
/** The ordered, visible status names shown in the lead dropdown (from a saved/merged config). */
export function visibleStatuses(raw) {
    return normalizeStatusConfig(raw)
        .filter((e) => !e.hidden)
        .map((e) => e.name);
}
export const EMPTY_QUALIFICATION = {
    verifyContact: '',
    title: '',
    providers: '',
    specialtyType: '',
    systemsUsed: '',
    currentBiller: '',
    otherDecisionMakers: '',
    leadInterest: '',
    painPoints: [],
    timeline: '',
    budget: '',
    discoveryAt: '',
    additionalNotes: '',
};
/** A lead qualifies for AE/FSE handoff at or above this readiness score. */
export const READINESS_THRESHOLD = 75;
const nonEmpty = (v) => !!(v && v.trim());
/**
 * Compute the 0–100 readiness score exactly as the previous version did (the right-rail panel on
 * the Qualify form): decision-maker verified +20, specific pain point +20, then timeline / budget-
 * authority / current-systems / discovery-date at +15 each.
 */
export function scoreReadiness(q) {
    const breakdown = [
        {
            key: 'decisionMaker',
            label: 'Decision maker verified (contact + title)',
            max: 20,
            met: nonEmpty(q.verifyContact) && nonEmpty(q.title),
            points: 0,
        },
        {
            key: 'painPoint',
            label: 'Specific pain point captured',
            max: 20,
            met: (q.painPoints?.length ?? 0) > 0 || nonEmpty(q.leadInterest),
            points: 0,
        },
        {
            key: 'timeline',
            label: 'Timeline stated',
            max: 15,
            met: nonEmpty(q.timeline),
            points: 0,
        },
        {
            key: 'budget',
            label: 'Budget / authority signal',
            max: 15,
            met: (nonEmpty(q.budget) && q.budget !== 'Unknown') || nonEmpty(q.otherDecisionMakers),
            points: 0,
        },
        {
            key: 'systems',
            label: 'Current systems / vendor captured',
            max: 15,
            met: nonEmpty(q.systemsUsed) || nonEmpty(q.currentBiller),
            points: 0,
        },
        {
            key: 'discovery',
            label: 'Discovery date/time set',
            max: 15,
            met: nonEmpty(q.discoveryAt),
            points: 0,
        },
    ];
    let score = 0;
    for (const c of breakdown) {
        c.points = c.met ? c.max : 0;
        score += c.points;
    }
    const band = score >= READINESS_THRESHOLD ? 'ready' : score >= 40 ? 'developing' : 'early';
    const bandLabel = band === 'ready'
        ? 'QL / AE-FSE READY'
        : band === 'developing'
            ? 'Developing — keep qualifying'
            : 'Early — more discovery needed';
    return { score, ready: band === 'ready', band, bandLabel, breakdown };
}
/** Merge-field token → the lead value it resolves to (aliases included so authors have latitude). */
const TEMPLATE_TOKENS = {
    company: (l) => l.company,
    first: (l) => l.first,
    firstname: (l) => l.first,
    last: (l) => l.last,
    lastname: (l) => l.last,
    name: (l) => [l.first, l.last].filter(Boolean).join(' '),
    title: (l) => l.title,
    city: (l) => l.city,
    state: (l) => l.state,
    specialty: (l) => l.specialty,
    vendorehr: (l) => l.vendorEHR,
    ehr: (l) => l.vendorEHR,
    vendorpm: (l) => l.vendorPM,
    pm: (l) => l.vendorPM,
    vendorlis: (l) => l.vendorLIS,
    lis: (l) => l.vendorLIS,
    billing: (l) => l.billing,
    physicians: (l) => l.physicians,
    providers: (l) => l.physicians,
    repname: (l) => l.repName,
    campaign: (l) => l.campaign,
};
/** The merge-field tokens a script author may use (canonical names, for docs/help text). */
export const SCRIPT_MERGE_FIELDS = [
    'company', 'first', 'last', 'name', 'title', 'city', 'state', 'specialty',
    'vendorEHR', 'vendorPM', 'vendorLIS', 'billing', 'physicians', 'repName', 'campaign',
];
/**
 * Resolve `{{token}}` merge fields in a call-script string against the current lead. Case-insensitive
 * token names; a blank value renders a visible `[token]` placeholder so the rep knows to fill it in,
 * and unknown tokens are left untouched. Pure — safe to unit-test and to run in the browser.
 */
export function renderTemplate(text, lead) {
    if (!text)
        return '';
    if (!lead)
        return text;
    return text.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (whole, rawKey) => {
        const resolver = TEMPLATE_TOKENS[rawKey.toLowerCase()];
        if (!resolver)
            return whole; // leave unknown tokens intact
        const v = resolver(lead);
        const s = v === null || v === undefined ? '' : String(v).trim();
        return s || `[${rawKey}]`;
    });
}
//# sourceMappingURL=logic.js.map